import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track, trackPageView } from './track';

// Non-invasive auto tracker: page views, element clicks & impressions via
// data-analytics-id attributes. Mounted once inside the public Layout.
export default function AnalyticsTracker() {
  const location = useLocation();
  const seenImpressions = useRef(new Set());
  const ioRef = useRef(null);

  // Page view on route change
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);

  // Global click delegation
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target.closest && e.target.closest('[data-analytics-id]');
      if (!el) return;
      track('click', {
        element_id: el.getAttribute('data-analytics-id'),
        element_name: el.getAttribute('data-analytics-name') || undefined,
        product_id: el.getAttribute('data-analytics-product') || undefined,
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Impression tracking via IntersectionObserver (>=50% for >=1s, once per session/element)
  useEffect(() => {
    const timers = new WeakMap();
    ioRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const id = el.getAttribute('data-analytics-id');
        const pid = el.getAttribute('data-analytics-product') || '';
        const key = `${id}:${pid}`;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (seenImpressions.current.has(key)) return;
          const t = setTimeout(() => {
            if (seenImpressions.current.has(key)) return;
            seenImpressions.current.add(key);
            track('impression', {
              element_id: id,
              element_name: el.getAttribute('data-analytics-name') || undefined,
              product_id: pid || undefined,
            });
            ioRef.current && ioRef.current.unobserve(el);
          }, 1000);
          timers.set(el, t);
        } else {
          const t = timers.get(el);
          if (t) { clearTimeout(t); timers.delete(el); }
        }
      });
    }, { threshold: [0, 0.5, 1] });

    const scan = () => {
      document.querySelectorAll('[data-analytics-impression]:not([data-ana-obs])').forEach((el) => {
        el.setAttribute('data-ana-obs', '1');
        ioRef.current.observe(el);
      });
    };
    scan();
    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(scan, 2000);

    return () => { mo.disconnect(); clearInterval(interval); ioRef.current && ioRef.current.disconnect(); };
  }, []);

  return null;
}
